/**
 * LE PARCOURS D'ACCUEIL D'UN NOUVEL ÉQUIPEMENT (Ines, 28/10/2026).
 *
 * « Une aide à l'accueil d'un nouvel équipement qui guide pas à pas : le côté achat,
 *  le côté personnel, l'équipement lui-même, et le relier à des réactifs, eux-mêmes
 *  reliés à un examen. » Le circuit a été confirmé : équipement → technique → examen,
 *  et réactif (lot, produit du catalogue) → équipement par défaut.
 *
 * ── Ce que ce module est, et n'est pas ──────────────────────────────────────────
 * Il COMPLÈTE le dossier de mise en service (`shared/miseEnService.ts`), il ne le
 * remplace pas : le dossier atteste que douze étapes ont été franchies, ce parcours
 * regarde la fiche et dit, rubrique par rubrique, ce qui est déjà relié et ce qui
 * reste à faire — chaque point désignant le bloc de l'onglet « Présentation » où il
 * se lit et se complète (spec 002, récit 9). Rien n'est stocké :
 * tout se DÉRIVE de l'état de la fiche, donc rien ne peut diverger de lui.
 *
 * ── Pourquoi il ne bloque rien ──────────────────────────────────────────────────
 * Une mise en service s'étale sur des semaines, au rythme des livraisons. Seul le
 * dossier (étapes bloquantes, autorisation du RQ) interdit l'usage. Ce parcours
 * informe : un point « à faire » est un travail, pas une interdiction.
 *
 * ── Les trois états ─────────────────────────────────────────────────────────────
 * `fait` · `afaire` · `sans_objet` (la nature de l'appareil l'écarte — un logiciel
 * n'a ni réactif ni garantie). Un `inconnu` existe pour ce qui se lit à distance et
 * n'est pas encore arrivé : afficher « à faire » avant la réponse serait affirmer.
 *
 * Module PUR : aucune dépendance, testé depuis `backend/tests` (préfixe `front-`).
 */

export type EtatPoint = 'fait' | 'afaire' | 'sans_objet' | 'inconnu';

/**
 * LES BLOCS DE L'ONGLET « PRÉSENTATION » (spec 002, récit 9 — Ines, 03/11/2026).
 *
 * Identification et Mise en service ne font plus qu'un onglet, et le parcours
 * n'y est plus une liste qui renvoie ailleurs : il devient L'ORDRE DES BLOCS.
 * Chaque point du parcours désigne le bloc de la même page où il se lit et se
 * complète — jamais un autre onglet, jamais un autre écran (un point « Ouvrir »
 * qui faisait quitter le dossier sans chemin de retour, inventaire §6).
 *
 * L'ordre ci-dessous est celui de la page. Les clés sont des adresses publiées
 * (`?onglet=presentation&bloc=<bloc>`) : on n'en renomme aucune.
 */
export const BLOCS_PRESENTATION = [
  // Achat
  'signaletique', 'fournisseur', 'acquisition', 'contrat', 'contacts',
  // Équipement
  'alimentation', 'documentation', 'logiciel', 'metrologie', 'plan',
  // Personnel
  'responsables', 'habilitations', 'postes',
  // Réactifs & examens
  'analytique',
  // Dossier de mise en service, puis la fin de vie et l'observation
  'mise-en-service', 'reforme', 'observation',
] as const;
export type BlocPresentation = typeof BLOCS_PRESENTATION[number];

export const estBlocPresentation = (v: unknown): v is BlocPresentation =>
  typeof v === 'string' && (BLOCS_PRESENTATION as readonly string[]).includes(v);

/** Où mène un point : TOUJOURS un bloc de la Présentation. */
export type CiblePoint = { type: 'bloc'; bloc: BlocPresentation };

/** La table point → bloc (tâche T039). Écrite une fois : le parcours et le test la lisent. */
export const BLOC_DU_POINT: Record<string, BlocPresentation> = {
  fournisseur: 'fournisseur',
  contrat: 'contrat',
  contacts: 'contacts',
  reception: 'acquisition',
  documentation: 'documentation',
  rubriques: 'signaletique',
  dossier: 'mise-en-service',
  maintenance: 'plan',
  responsable: 'responsables',
  formation: 'documentation',
  habilitations: 'habilitations',
  techniques: 'analytique',
  reactifs: 'analytique',
  cqi: 'analytique',
};

const versBloc = (cle: string): CiblePoint => ({ type: 'bloc', bloc: BLOC_DU_POINT[cle] });

/**
 * LES ANCIENNES ADRESSES DU DOSSIER (`?onglet=identification`, `?onglet=mes`).
 *
 * Une adresse publiée est un contrat : elle mène désormais à la Présentation,
 * au bloc qui tient ce que l'ancien onglet montrait. `null` : l'onglet n'est pas
 * une ancienne clé — il se lit tel quel.
 */
export function redirectionOngletEquipement(onglet: string | null | undefined): { onglet: 'presentation'; bloc: BlocPresentation } | null {
  if (onglet === 'identification') return { onglet: 'presentation', bloc: 'signaletique' };
  if (onglet === 'mes') return { onglet: 'presentation', bloc: 'mise-en-service' };
  return null;
}

/**
 * L'ÉTAT D'UN BLOC, lu dans les points du parcours qui le désignent.
 *
 * Un « à faire » l'emporte (le bloc a du travail), puis un « pas encore lu »
 * (afficher « fait » sur une lecture absente serait affirmer), puis « fait ».
 * Un bloc dont tous les points sont écartés est « sans objet ». `null` : aucun
 * point ne le désigne — le bloc n'a pas d'état dérivé du parcours.
 */
export function etatBloc(pans: Pan[], bloc: BlocPresentation): EtatPoint | null {
  const points = pans.flatMap((p) => p.points).filter((x) => x.cible?.bloc === bloc);
  if (!points.length) return null;
  const retenus = points.filter((x) => x.etat !== 'sans_objet');
  if (!retenus.length) return 'sans_objet';
  if (retenus.some((x) => x.etat === 'afaire')) return 'afaire';
  if (retenus.some((x) => x.etat === 'inconnu')) return 'inconnu';
  return 'fait';
}

export interface Point {
  cle: string;
  libelle: string;
  etat: EtatPoint;
  /** Une phrase : ce qui est relié, ou ce qui manque et pourquoi cela compte. */
  detail: string;
  cible?: CiblePoint;
}

export interface Pan {
  cle: 'achat' | 'equipement' | 'personnel' | 'analytique';
  titre: string;
  points: Point[];
}

/** Ce que la fiche et son environnement donnent. Tout est optionnel : une fiche
 *  ancienne ne porte pas tout, et `null` signifie « pas encore lu ». */
export interface EntreeAccueil {
  nature?: string | null;
  categorie?: string | null;
  code?: string | null;
  fournisseur?: string | null;
  fournisseurCode?: string | null;
  contrat?: { societe?: string; societeCode?: string; fin?: string } | null;
  contactTechnique?: { societeCode?: string; techniciens?: { nom?: string }[] } | null;
  contacts?: unknown[] | null;
  dateReception?: string | null;
  garantieJusquA?: string | null;
  documentation?: {
    manuel?: boolean | null; documentsTechniques?: boolean | null;
    formationUtilisateur?: boolean | null; formationMaintenance?: boolean | null;
  } | null;
  responsableNom?: string | null;
  suppleantNom?: string | null;
  dossierMesOuvert: boolean;
  /** Libellés des rubriques §6.4.2 encore vides (`rubriquesManquantes`). */
  rubriquesManquantes: string[];
  /** Techniques actives qui nomment cet appareil. `null` : pas encore lu. */
  techniques: { code: string; nom: string; nbExamens: number | null }[] | null;
  /** Produits du catalogue dont l'équipement par défaut est celui-ci. `null` : pas encore lu. */
  produits: { code: string; libelle: string; nbExamens: number }[] | null;
  /** Lignes de plan de maintenance actives (registre des maintenances). `null` : registre pas encore lu. */
  nbPlansMaintenance: number | null;
  /** Lots reçus pour cet appareil (réactifs, contrôles, calibrants). */
  nbLots: number;
  /** Cartes de contrôle interne ouvertes sur cet appareil. */
  nbCartes: number;
  /** Personnes habilitées sur les tâches de cet appareil. `null` : pas encore lu. */
  nbHabilites: number | null;
  /** Combien de tâches d'habilitation nomment cet appareil. `null` : pas encore lu. */
  nbHabilitations: number | null;
}

const txt = (v: unknown) => String(v ?? '').trim();
const pl = (n: number, un: string, plusieurs = `${un}s`) => `${n} ${n > 1 ? plusieurs : un}`;

/** Une garantie est un fait d'ACHAT d'un objet : ni un logiciel ni un accessoire n'en portent. */
const porteAchatMateriel = (nature: string) => nature !== 'logiciel';
/** Seul un appareil qui PRODUIT un résultat passe des réactifs, des contrôles et des examens. */
const produitUnResultat = (nature: string) => nature === 'mesure';

export function parcoursAccueil(e: EntreeAccueil): Pan[] {
  const nature = txt(e.nature) || 'mesure';
  const doc = e.documentation || null;

  // ── ACHAT ────────────────────────────────────────────────────────────────────
  const fournisseurRelie = txt(e.fournisseurCode) !== '';
  const fournisseurTexte = txt(e.fournisseur) !== '';
  const contratRelie = txt(e.contrat?.societeCode) !== '';
  const contratTexte = txt(e.contrat?.societe) !== '';
  const aDesContacts = (e.contactTechnique?.techniciens || []).some((t) => txt(t.nom) !== '')
    // Sur les postes informatiques, `contacts` est un objet (reliquat de reprise) : seule une liste compte.
    || (Array.isArray(e.contacts) && e.contacts.length > 0);

  const achat: Point[] = [
    {
      cle: 'fournisseur',
      libelle: 'Fournisseur relié à l’annuaire',
      etat: fournisseurRelie ? 'fait' : 'afaire',
      detail: fournisseurRelie ? 'La société est celle de l’annuaire : son téléphone se tient à jour à un seul endroit.'
        : fournisseurTexte ? 'Un nom est saisi mais pas relié : choisissez la société dans l’annuaire.'
          : 'Aucun fournisseur : c’est lui qu’on appelle en panne.',
          },
    {
      cle: 'contrat',
      libelle: 'Contrat de maintenance',
      etat: contratRelie ? 'fait' : 'afaire',
      detail: contratRelie ? 'Société et échéance du contrat connues.'
        : contratTexte ? 'Société saisie en texte : à relier à l’annuaire.'
          : 'Aucun contrat renseigné — ou l’appareil n’en a pas, ce que la fiche doit alors dire.',
          },
    {
      cle: 'contacts',
      libelle: 'Contact technique / SAV',
      etat: aDesContacts ? 'fait' : 'afaire',
      detail: aDesContacts ? 'Au moins un interlocuteur à appeler.' : 'Aucun technicien ni contact : à 7 h du matin, personne à joindre.',
          },
    {
      cle: 'reception',
      libelle: 'Réception et garantie',
      etat: !porteAchatMateriel(nature) ? 'sans_objet' : txt(e.dateReception) ? 'fait' : 'afaire',
      detail: !porteAchatMateriel(nature) ? 'Un logiciel ne se réceptionne pas comme un objet.'
        : txt(e.dateReception)
          ? (txt(e.garantieJusquA) ? 'Réception datée, garantie connue.' : 'Réception datée ; la fin de garantie n’est pas renseignée.')
          : 'Date de réception absente : le procès-verbal de réception en dépend.',
          },
    {
      cle: 'documentation',
      libelle: 'Manuel et documents techniques',
      etat: doc?.manuel === true && doc?.documentsTechniques === true ? 'fait' : 'afaire',
      detail: doc?.manuel === true && doc?.documentsTechniques === true ? 'Fournis par le fabricant.'
        : 'À obtenir à la livraison : on ne les réclame plus après.',
          },
  ];

  // ── ÉQUIPEMENT ───────────────────────────────────────────────────────────────
  const manque = e.rubriquesManquantes || [];
  const equipement: Point[] = [
    {
      cle: 'rubriques',
      libelle: 'Fiche signalétique (ISO 15189 §6.4.2)',
      etat: manque.length ? 'afaire' : 'fait',
      detail: manque.length ? `Il manque : ${manque.join(', ')}.` : 'Les six rubriques exigées sont renseignées.',
          },
    {
      cle: 'dossier',
      libelle: 'Dossier de mise en service ouvert',
      etat: e.dossierMesOuvert ? 'fait' : 'afaire',
      detail: e.dossierMesOuvert ? 'Les étapes se cochent au fil des semaines.'
        : 'Tant qu’il n’est pas ouvert, rien ne dit que l’appareil n’est pas encore utilisable.',
          },
    {
      cle: 'maintenance',
      libelle: 'Plan de maintenance',
      etat: nature === 'logiciel' ? 'sans_objet' : e.nbPlansMaintenance === null ? 'inconnu' : e.nbPlansMaintenance > 0 ? 'fait' : 'afaire',
      detail: nature === 'logiciel' ? 'Un logiciel se met à jour, il ne se maintient pas.'
        : e.nbPlansMaintenance === null ? 'Pas encore lu.'
          : e.nbPlansMaintenance > 0 ? `${pl(e.nbPlansMaintenance, 'ligne')} de plan active${e.nbPlansMaintenance > 1 ? 's' : ''} : l’échéance se calcule.`
            : 'Aucune ligne de plan : aucune échéance de maintenance ne sera jamais calculée pour cet appareil.',
          },
  ];

  // ── PERSONNEL ────────────────────────────────────────────────────────────────
  const responsable = txt(e.responsableNom) !== '';
  const suppleant = txt(e.suppleantNom) !== '';
  const formes = doc?.formationUtilisateur === true;
  const personnel: Point[] = [
    {
      cle: 'responsable',
      libelle: 'Responsable et suppléant désignés',
      etat: responsable && suppleant ? 'fait' : 'afaire',
      detail: responsable && suppleant ? 'Un titulaire et un remplaçant.'
        : responsable ? 'Il manque un suppléant : qui répond le jour où le responsable est absent ?'
          : 'Aucun responsable : personne n’est comptable de l’appareil.',
          },
    {
      cle: 'formation',
      libelle: 'Formation des utilisateurs',
      etat: formes ? 'fait' : 'afaire',
      detail: formes ? 'La formation initiale est faite.' : 'À faire par le fournisseur à l’installation, avant le premier échantillon.',
          },
    {
      cle: 'habilitations',
      libelle: 'Personnes habilitées',
      etat: e.nbHabilites === null ? 'inconnu' : e.nbHabilites > 0 ? 'fait' : 'afaire',
      detail: e.nbHabilites === null ? 'Pas encore lu.'
        : e.nbHabilites > 0 ? `${pl(e.nbHabilites, 'personne habilitée', 'personnes habilitées')} sur les tâches de cet appareil.`
          : (e.nbHabilitations ?? 0) > 0
            ? 'Les tâches existent mais personne n’en détient la totalité.'
            : 'Aucune tâche d’habilitation ne nomme cet appareil : à écrire dans le référentiel.',
          },
  ];

  // ── ANALYTIQUE ───────────────────────────────────────────────────────────────
  const resultat = produitUnResultat(nature);
  const nbTech = e.techniques?.length ?? 0;
  /* ⚠️ `null` = les examens n'ont pas été lus (la relation peut être refusée au lecteur sans que la technique le soit) :
     annoncer « 0 examen » serait affirmer un vide. On ne compte que si TOUTES les techniques l'ont dit. */
  const nbExamens = (e.techniques || []).every((t) => t.nbExamens !== null)
    ? (e.techniques || []).reduce((s, t) => s + (t.nbExamens as number), 0) : null;
  const nbProduits = e.produits?.length ?? 0;
  const reactifsRelies = nbProduits + e.nbLots;
  const analytique: Point[] = [
    {
      cle: 'techniques',
      libelle: 'Techniques et examens réalisés',
      etat: !resultat ? 'sans_objet' : e.techniques === null ? 'inconnu' : nbTech > 0 ? 'fait' : 'afaire',
      detail: !resultat ? 'Cet appareil ne rend pas de résultat sur un échantillon.'
        : e.techniques === null ? 'Pas encore lu.'
          : nbTech > 0 ? `${pl(nbTech, 'technique')}${nbExamens === null ? '' : `, ${pl(nbExamens, 'examen')}`} : ${e.techniques.map((t) => t.code).join(', ')}.`
            : 'Aucune technique ne nomme cet appareil : aucun examen n’est rattaché à lui dans le référentiel.',
          },
    {
      cle: 'reactifs',
      libelle: 'Réactifs reliés',
      etat: !resultat ? 'sans_objet' : e.produits === null ? 'inconnu' : reactifsRelies > 0 ? 'fait' : 'afaire',
      detail: !resultat ? 'Pas de réactif propre à cet appareil.'
        : e.produits === null ? 'Pas encore lu.'
          : reactifsRelies > 0
            ? `${pl(nbProduits, 'produit du catalogue')} l’ont pour appareil par défaut${e.nbLots ? `, ${pl(e.nbLots, 'lot reçu', 'lots reçus')}` : ''}.`
            : 'Aucun produit du catalogue ni lot ne le désigne : la réception d’un lot ne le proposera pas.',
          },
    {
      cle: 'cqi',
      libelle: 'Carte de contrôle interne',
      etat: !resultat ? 'sans_objet' : e.nbCartes > 0 ? 'fait' : 'afaire',
      detail: !resultat ? 'Seul un appareil qui mesure porte une carte de contrôle.'
        : e.nbCartes > 0 ? `${pl(e.nbCartes, 'carte')} ouverte${e.nbCartes > 1 ? 's' : ''}.`
          : 'Aucune carte : sans contrôle interne, aucun résultat ne devrait sortir.',
          },
  ];

  /* Chaque point mène à SON bloc de la Présentation (table `BLOC_DU_POINT`). */
  const cibler = (pts: Point[]) => pts.map((p) => ({ ...p, cible: versBloc(p.cle) }));
  return [
    { cle: 'achat', titre: 'Achat', points: cibler(achat) },
    { cle: 'equipement', titre: 'Équipement', points: cibler(equipement) },
    { cle: 'personnel', titre: 'Personnel', points: cibler(personnel) },
    { cle: 'analytique', titre: 'Réactifs & examens', points: cibler(analytique) },
  ];
}

/** Le bilan d'un pan : ce qui est fait sur ce qui compte (le sans objet n'entre pas). */
export function bilanPan(p: Pan): { faits: number; total: number; inconnus: number } {
  const retenus = p.points.filter((x) => x.etat !== 'sans_objet');
  return {
    faits: retenus.filter((x) => x.etat === 'fait').length,
    total: retenus.length,
    inconnus: retenus.filter((x) => x.etat === 'inconnu').length,
  };
}

/** Le premier pan qui n'est pas terminé — celui vers lequel on pousse. `null` : tout est relié. */
export function panSuivant(pans: Pan[]): Pan | null {
  return pans.find((p) => p.points.some((x) => x.etat === 'afaire')) || null;
}
